/**
 * DocSidebarCoverMediaSection Component
 * 
 * Displays the cover image/video section of the document sidebar.
 * Handles auto-rotation between image and video, pause/resume controls,
 * and cover image upload.
 */

import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ImageIcon, Pause, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUpdateCoverImage } from "../api/useUpdateCoverImage";

const IMAGE_DISPLAY_DURATION = 5000; // 5 seconds
const FADE_TRANSITION_DURATION = 500; // 500ms fade

type DocSidebarCoverMediaSectionProps = {
  doctype: string;
  name: string;
  image?: string | null;
  primaryVideo?: { url: string } | null;
  editable?: boolean;
  onImageClick?: () => void;
};

export function DocSidebarCoverMediaSection({
  doctype,
  name,
  image,
  primaryVideo,
  editable = false,
  onImageClick,
}: DocSidebarCoverMediaSectionProps) {
  const [currentMedia, setCurrentMedia] = useState<'image' | 'video'>('image');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageDisplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const { updateCoverImage, isLoading: isUpdatingCoverImage } = useUpdateCoverImage(doctype, name);

  // Auto-rotation effect: switch between image and video
  useEffect(() => {
    // If no image and no video, nothing to show
    if (!image && !primaryVideo) {
      return;
    }
    
    // If no video, always show image (if image exists)
    if (!primaryVideo) {
      setCurrentMedia('image');
      setIsTransitioning(false);
      return;
    }
    
    // If no image but video exists, show video only (no rotation)
    if (!image && primaryVideo) {
      setCurrentMedia('video');
      setIsTransitioning(false);
      return;
    }
    
    // Both image and video exist - enable rotation (only if not paused)
    if (currentMedia === 'image' && !isPaused) {
      // Set timer to switch to video after IMAGE_DISPLAY_DURATION
      imageDisplayTimerRef.current = setTimeout(() => {
        setIsTransitioning(true);
        fadeTimeoutRef.current = setTimeout(() => {
          setCurrentMedia('video');
          setIsTransitioning(false);
          fadeTimeoutRef.current = null;
          // Auto-play video when switching
          videoRef.current?.play().catch((err) => {
            console.warn('[DocSidebar] Video autoplay failed:', err);
          });
        }, FADE_TRANSITION_DURATION / 2);
      }, IMAGE_DISPLAY_DURATION);
      
      return () => {
        if (imageDisplayTimerRef.current) {
          clearTimeout(imageDisplayTimerRef.current);
          imageDisplayTimerRef.current = null;
        }
        if (fadeTimeoutRef.current) {
          clearTimeout(fadeTimeoutRef.current);
          fadeTimeoutRef.current = null;
        }
      };
    }
    // If currentMedia is 'video', the video's onEnded handler will switch back to image
  }, [currentMedia, image, primaryVideo, isPaused]);
  
  // Handle video end: switch back to image
  const handleVideoEnded = () => {
    if (isPaused) return; // Don't switch if paused
    setIsTransitioning(true);
    // Clear any existing fade timeout
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
    }
    fadeTimeoutRef.current = setTimeout(() => {
      setCurrentMedia('image');
      setIsTransitioning(false);
      fadeTimeoutRef.current = null;
    }, FADE_TRANSITION_DURATION / 2);
  };
  
  // Toggle pause/resume
  const handleTogglePause = () => {
    const newPausedState = !isPaused;
    setIsPaused(newPausedState);
    
    if (newPausedState) {
      // Pause: clear timers and pause video
      if (imageDisplayTimerRef.current) {
        clearTimeout(imageDisplayTimerRef.current);
        imageDisplayTimerRef.current = null;
      }
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
      if (currentMedia === 'video' && videoRef.current) {
        videoRef.current.pause();
      }
    } else {
      // Resume: continue rotation
      if (currentMedia === 'image') {
        // Restart timer to switch to video
        imageDisplayTimerRef.current = setTimeout(() => {
          setIsTransitioning(true);
          fadeTimeoutRef.current = setTimeout(() => {
            setCurrentMedia('video');
            setIsTransitioning(false);
            fadeTimeoutRef.current = null;
            videoRef.current?.play().catch((err) => {
              console.warn('[DocSidebar] Video autoplay failed:', err);
            });
          }, FADE_TRANSITION_DURATION / 2);
        }, IMAGE_DISPLAY_DURATION);
      } else if (currentMedia === 'video' && videoRef.current) {
        // Resume video playback
        videoRef.current.play().catch((err) => {
          console.warn('[DocSidebar] Video play failed:', err);
        });
      }
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (imageDisplayTimerRef.current) {
        clearTimeout(imageDisplayTimerRef.current);
        imageDisplayTimerRef.current = null;
      }
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
    };
  }, []);

  const handleChangeCoverImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      await updateCoverImage(file);
      e.target.value = ""; // Reset input
    }
  };

  return (
    <div className="overflow-hidden rounded-lg relative">
      <div className="w-full bg-white dark:bg-white flex items-center justify-center overflow-hidden relative">
        {/* Spacer/Placeholder to maintain container height */}
        {(image || primaryVideo) && (
          <div className="w-full aspect-square opacity-0 pointer-events-none" aria-hidden="true">
            {image && (
              <img
                src={image}
                alt=""
                className="w-full h-full object-cover"
                aria-hidden="true"
              />
            )}
          </div>
        )}
        
        {/* Image */}
        {image && (
          <div
            className={cn(
              "absolute inset-0 w-full h-full transition-opacity duration-500",
              currentMedia === 'image' && !isTransitioning ? "opacity-100 z-10" : "opacity-0 z-0"
            )}
          >
            <img
              src={image}
              alt={name}
              className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
              onClick={onImageClick}
              onError={(e) => {
                // Hide image on error, show fallback
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        )}
        
        {/* Video */}
        {primaryVideo && (
          <div
            className={cn(
              "absolute inset-0 w-full h-full transition-opacity duration-500",
              currentMedia === 'video' && !isTransitioning ? "opacity-100 z-10" : "opacity-0 z-0"
            )}
          >
            <video
              ref={videoRef}
              src={primaryVideo.url}
              muted
              playsInline
              className="w-full h-full object-cover"
              onEnded={handleVideoEnded}
              onError={() => {
                // On video error, fallback to image
                console.warn('[DocSidebar] Video error, falling back to image');
                setCurrentMedia('image');
              }}
            />
            {/* Pause button overlay - only show when video is visible */}
            {currentMedia === 'video' && !isTransitioning && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute bottom-3 right-3 h-8 w-8 rounded-full bg-black/50 backdrop-blur hover:bg-black/70 text-white"
                onClick={handleTogglePause}
              >
                {isPaused ? (
                  <Play className="h-4 w-4 ml-0.5" />
                ) : (
                  <Pause className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        )}
        
        {/* Fallback: No image or video */}
        {!image && !primaryVideo && (
          <div className="w-full aspect-square flex items-center justify-center">
            <span className="text-xs text-muted-foreground">No image</span>
          </div>
        )}
      </div>
      {editable && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-3 right-3 sm:top-4 sm:right-4 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-background/90 backdrop-blur hover:bg-background touch-manipulation"
            onClick={() => imageInputRef.current?.click()}
            disabled={isUpdatingCoverImage}
          >
            <ImageIcon className="h-5 w-5 sm:h-6 sm:w-6" />
          </Button>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleChangeCoverImage}
          />
        </>
      )}
    </div>
  );
}
